/**
 * S17 — Measure · Model Performance & Drift (`/measure/performance`).
 *
 * Catch quality / hallucination / latency drift across every governed
 * agent before it becomes an incident. The page renders 12-week
 * sparklines per metric, surfaces signal status (ok / warning / alert)
 * and offers a focus drawer to inspect any agent in depth.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Activity,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  X,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntity } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { cn } from "@/lib/utils";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";

const STATUS_TABS = [
  { id: "all",     label: "All" },
  { id: "alert",   label: "Alert" },
  { id: "warning", label: "Warning" },
  { id: "ok",      label: "OK" },];

const SORTS = [
  { id: "drift-desc",   label: "Drift score · high → low", cmp: (a, b) => b.driftScore - a.driftScore },
  { id: "quality-asc",  label: "Quality · low → high",     cmp: (a, b) => last(a.answerQuality) - last(b.answerQuality) },
  { id: "halluc-desc",  label: "Hallucination · high first", cmp: (a, b) => last(b.hallucinationRate) - last(a.hallucinationRate) },
  { id: "latency-desc", label: "Latency · slow first",     cmp: (a, b) => last(b.p95LatencyMs) - last(a.p95LatencyMs) },
];

const last = (a) => (a && a.length ? a[a.length - 1] : 0);
const first = (a) => (a && a.length ? a[0] : 0);
const delta = (a) => last(a) - first(a);

const POLICY_THRESHOLDS = { quality: 85, halluc: 5.0, latency: 1200 };

export default function ModelPerformance() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    data,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntity("/api/metrics/performance");
  const driftSignals = useMemo(() => data?.driftSignals || [], [data]);
  const navigate = useNavigate();

  const [statusTab, setStatusTab] = useState("all");
  const [search, setSearch] = useState("");
  const [sortId, setSortId] = useState("drift-desc");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cmp = SORTS.find((s) => s.id === sortId)?.cmp || SORTS[0].cmp;
    return driftSignals
      .filter((s) => {
        if (statusTab !== "all" && s.status !== statusTab) return false;
        if (q && !`${s.agentId} ${s.agentName}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice()
      .sort(cmp);
  }, [driftSignals, statusTab, search, sortId]);

  const counts = useMemo(() => ({
    alerts:   driftSignals.filter((s) => s.status === "alert").length,
    warnings: driftSignals.filter((s) => s.status === "warning").length,
    healthy:  driftSignals.filter((s) => s.status === "ok").length,
    avgQuality: driftSignals.length
      ? +(driftSignals.reduce((acc, s) => acc + last(s.answerQuality), 0) / driftSignals.length).toFixed(1)
      : 0,
  }), [driftSignals]);

  const resetFilters = useCallback(() => {
    setStatusTab("all"); setSearch(""); setSortId("drift-desc");
  }, []);

  if (loading && !data) return <Skeleton />;
  if (error) return <ErrorView message={error.message} onRetry={refetch} />;
  if (isCustomer && driftSignals.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSetup={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="model-performance">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header lastSyncedAt={lastSyncedAt} loading={loading} onRefetch={refetch} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile label="Alerting agents"     value={counts.alerts.toString()}      delta={counts.alerts > 0 ? "Investigate" : "All clear"} deltaTone={counts.alerts > 0 ? "down" : "up"} hint="hard policy threshold breached" icon={AlertTriangle} tone="risk"     testId="drift-kpi-alerts" />
        <KpiTile label="Warning agents"      value={counts.warnings.toString()}    delta="trending toward threshold"        deltaTone="neutral"                                       hint="soft warning band"                icon={TrendingDown}   tone="gold"     testId="drift-kpi-warnings" />
        <KpiTile label="Healthy agents"      value={counts.healthy.toString()}     delta="within policy band"               deltaTone="up"                                            hint="no drift signal"                    icon={Activity}       tone="low"     testId="drift-kpi-healthy" />
        <KpiTile label="Avg answer quality"  value={`${counts.avgQuality}%`}       delta="across all governed agents"       deltaTone="neutral"                                       hint="rolling 12-week mean"               icon={Activity}       tone="tralenex" testId="drift-kpi-avg-quality" />
      </div>

      <StatusTabs current={statusTab} setCurrent={setStatusTab} items={driftSignals} />

      <div className="mt-4 surface-card shadow-e1 overflow-hidden" data-testid="drift-panel">
        <Toolbar
          search={search} setSearch={setSearch}
          sortId={sortId} setSortId={setSortId}
          matchCount={filtered.length} totalCount={driftSignals.length}
          onReset={resetFilters}
        />
        <Table items={filtered} onSelect={setSelected} />
      </div>

      <FocusDrawer
        item={selected}
        onClose={() => setSelected(null)}
        onOpenAgent={(id) => navigate(`/govern/agents/${id}`)}
      />
    </div>
  );
}

function Header({ lastSyncedAt, loading, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Measure · Model Performance & Drift</p>
        <h1 className="text-display-md text-ink-primary mt-1">Performance & Drift</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          Catch quality, hallucination and latency drift across every governed
          agent before it becomes an incident. Each row is a 12-week trend
          with a status verdict against the drift policy (POL-…-108).
        </p>
      </div>
      <SyncedIndicator
        lastSyncedAt={lastSyncedAt}
        loading={loading}
        onRefetch={onRefetch}
        testId="model-performance-synced"
      />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="model-performance-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[400px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="model-performance-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Measure · Performance & Drift</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load drift signals</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="model-performance-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function StatusTabs({ current, setCurrent, items }) {
  const counts = useMemo(() => {
    const c = { all: items.length, alert: 0, warning: 0, ok: 0 };
    items.forEach((i) => { if (i.status in c) c[i.status] += 1; });
    return c;
  }, [items]);
  return (
    <div className="mt-6 inline-flex items-center gap-1 border border-border-subtle rounded-md p-1 bg-inset/30" data-testid="drift-status-tabs">
      {STATUS_TABS.map((t) => {
        const active = current === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setCurrent(t.id)}
            data-testid={`drift-tab-${t.id}`}
            className={cn(
              "px-3 h-8 rounded text-sm font-medium tabular-nums transition-colors",
              active ? "bg-white text-ink-primary shadow-e1" : "text-ink-secondary hover:text-ink-primary",
            )}
          >
            {t.label}
            <span className="ml-1.5 text-meta text-ink-disabled">{counts[t.id]}</span>
          </button>
        );
      })}
    </div>
  );
}

function Toolbar({ search, setSearch, sortId, setSortId, matchCount, totalCount, onReset }) {
  return (
    <div className="px-4 py-3 border-b border-border-subtle flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by agent name or ID…"
          data-testid="drift-search"
          className="w-full pl-8 pr-3 h-9 rounded-md border border-border-subtle bg-white text-sm placeholder:text-ink-disabled focus:outline-none focus:border-tralenex-500"
        />
      </div>
      <select value={sortId} onChange={(e) => setSortId(e.target.value)} data-testid="drift-sort-select" className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2">
        {SORTS.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
      </select>
      <button type="button" onClick={onReset} data-testid="drift-reset" className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-border-subtle text-sm text-ink-secondary hover:text-ink-primary">
        <RefreshCw size={12} /> Reset
      </button>
      <span className="ml-auto text-meta text-ink-secondary tabular-nums" data-testid="drift-count">
        {matchCount} of {totalCount}
      </span>
    </div>
  );
}

function Table({ items, onSelect }) {
  if (!items.length) {
    return (
      <div className="py-16 text-center text-ink-secondary" data-testid="drift-empty">
        No agents match the current filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="drift-table">
        <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
          <tr>
            {["Agent", "Status", "Quality (12w)", "Hallucination (12w)", "P95 latency (12w)", "Drift score", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((s) => <Row key={s.agentId} item={s} onSelect={onSelect} />)}
        </tbody>
      </table>
    </div>
  );
}

function Row({ item, onSelect }) {
  const q = last(item.answerQuality);
  const h = last(item.hallucinationRate);
  const l = last(item.p95LatencyMs);
  const dq = delta(item.answerQuality);
  const dh = delta(item.hallucinationRate);
  const dl = delta(item.p95LatencyMs);
  return (
    <tr className="border-t border-border-subtle hover:bg-inset/30 cursor-pointer align-middle" data-testid={`drift-row-${item.agentId}`} onClick={() => onSelect(item)}>
      <td className="px-3 py-2 min-w-[220px]">
        <p className="text-ink-primary font-medium">{item.agentName}</p>
        <p className="text-meta font-mono text-ink-secondary">{item.agentId}</p>
      </td>
      <td className="px-3 py-2 whitespace-nowrap"><StatusPill status={item.status} /></td>
      <td className="px-3 py-2">
        <MetricCell value={`${q}%`} delta={dq} sparkData={item.answerQuality} thresholdLow={POLICY_THRESHOLDS.quality} inverted />
      </td>
      <td className="px-3 py-2">
        <MetricCell value={`${h.toFixed(1)}%`} delta={dh} sparkData={item.hallucinationRate} thresholdHigh={POLICY_THRESHOLDS.halluc} />
      </td>
      <td className="px-3 py-2">
        <MetricCell value={`${Math.round(l)}ms`} delta={Math.round(dl)} sparkData={item.p95LatencyMs} thresholdHigh={POLICY_THRESHOLDS.latency} />
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-ink-primary font-semibold">{item.driftScore.toFixed(1)}</td>
      <td className="px-3 py-2 text-meta text-ink-disabled whitespace-nowrap"><ArrowUpRight size={12} /></td>
    </tr>
  );
}

function StatusPill({ status }) {
  const map = {
    alert:   { label: "alert",   cls: "bg-risk-high/10 text-risk-high" },
    warning: { label: "warning", cls: "bg-gold-300/15 text-gold-700" },
    ok:      { label: "ok",      cls: "bg-risk-low/10 text-risk-low" },
  };
  const m = map[status] || map.ok;
  return (
    <span data-testid={`drift-status-${status}`} className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase", m.cls)}>
      {m.label}
    </span>
  );
}

function deltaClassFor(d, goodDirection) {
  if (Math.abs(d) < 0.01) return "text-ink-disabled";
  return goodDirection ? "text-risk-low" : "text-risk-high";
}

function MetricCell({ value, delta: d, sparkData, thresholdHigh, thresholdLow, inverted }) {
  const breached =
    (thresholdHigh != null && last(sparkData) > thresholdHigh) ||
    (thresholdLow != null && last(sparkData) < thresholdLow);
  // For quality (inverted = true): up = good. For halluc/latency: up = bad.
  const isUp = d > 0;
  const goodDirection = inverted ? isUp : !isUp;
  const deltaCls = deltaClassFor(d, goodDirection);
  const deltaSign = isUp ? "+" : "";
  const deltaText = typeof d === "number" ? `${deltaSign}${(Math.abs(d) >= 100 ? d.toFixed(0) : d.toFixed(1))}` : "—";
  return (
    <div className="flex items-center gap-2">
      <div className="leading-tight min-w-[64px]">
        <p className={cn("tabular-nums text-ink-primary font-semibold", breached && "text-risk-high")}>{value}</p>
        <p className={cn("text-meta tabular-nums", deltaCls)}>Δ {deltaText}</p>
      </div>
      <Sparkline data={sparkData} breached={breached} />
    </div>
  );
}

function Sparkline({ data, breached }) {
  if (!data || data.length < 2) return null;
  const w = 96, h = 28, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const points = data.map((v, i) => {
    const x = pad + (i * (w - pad * 2)) / (data.length - 1);
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const stroke = breached ? "#dc2626" : "#0f766e";
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden="true">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
}

function FocusDrawer({ item, onClose, onOpenAgent }) {
  if (!item) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} data-testid="drift-drawer-backdrop" />
      <aside className="fixed top-0 right-0 h-full w-full max-w-[540px] bg-white border-l border-border-subtle z-50 overflow-y-auto shadow-2xl" data-testid="drift-drawer">
        <header className="px-6 py-5 border-b border-border-subtle sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-meta font-mono text-ink-secondary">{item.agentId}</p>
              <h2 className="mt-1 text-h2 text-ink-primary leading-snug" data-testid="drift-drawer-title">{item.agentName}</h2>
              <div className="mt-2 flex items-center gap-2">
                <StatusPill status={item.status} />
                <span className="text-meta text-ink-secondary">Last checked {item.lastChecked}</span>
              </div>
            </div>
            <button type="button" onClick={onClose} className="text-ink-secondary hover:text-ink-primary p-1" data-testid="drift-drawer-close"><X size={18} /></button>
          </div>
        </header>
        <div className="px-6 py-5 space-y-6">
          <BigChart label="Answer quality" suffix="%" data={item.answerQuality} thresholdLow={POLICY_THRESHOLDS.quality} inverted />
          <BigChart label="Hallucination rate" suffix="%" data={item.hallucinationRate} thresholdHigh={POLICY_THRESHOLDS.halluc} />
          <BigChart label="P95 latency" suffix="ms" data={item.p95LatencyMs} thresholdHigh={POLICY_THRESHOLDS.latency} />
          <div className="rounded-md border border-border-subtle bg-inset/30 p-3 text-sm text-ink-primary leading-snug">
            <p className="eyebrow">Drift verdict</p>
            <p className="mt-1">
              Composite drift score <span className="font-mono">{item.driftScore.toFixed(1)}</span>{" "}
              {item.status === "alert" && "exceeds the hard policy ceiling — an incident has already been raised."}
              {item.status === "warning" && "is within the soft-warning band — investigate before it breaches."}
              {item.status === "ok" && "is well within the policy band — no action required."}
            </p>
          </div>
        </div>
        <footer className="px-6 py-4 border-t border-border-subtle sticky bottom-0 bg-white">
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-border-subtle text-sm text-ink-secondary hover:text-ink-primary">Close</button>
            <button type="button" onClick={() => onOpenAgent(item.agentId)} data-testid="drift-drawer-open-agent" className="h-9 px-3 rounded-md text-sm font-semibold inline-flex items-center gap-1 bg-tralenex-600 text-white hover:bg-tralenex-700">
              Open agent <ArrowUpRight size={14} />
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function BigChart({ label, suffix, data, thresholdLow, thresholdHigh, inverted }) {
  const w = 460, h = 90, pad = 6;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const points = data.map((v, i) => {
    const x = pad + (i * (w - pad * 2)) / (data.length - 1);
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const breached =
    (thresholdHigh != null && last(data) > thresholdHigh) ||
    (thresholdLow  != null && last(data) < thresholdLow);
  const stroke = breached ? "#dc2626" : "#0f766e";
  const d = last(data) - first(data);
  const isUp = d > 0;
  const goodDirection = inverted ? isUp : !isUp;
  const deltaCls = deltaClassFor(d, goodDirection);
  return (
    <div data-testid={`drift-chart-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between">
        <p className="eyebrow">{label}</p>
        <p className={cn("text-sm font-semibold tabular-nums", breached ? "text-risk-high" : "text-ink-primary")}>
          {last(data).toFixed(suffix === "ms" ? 0 : 1)}{suffix}
        </p>
      </div>
      <div className="mt-2 rounded-md border border-border-subtle bg-white p-2">
        <svg width="100%" viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
          <polyline points={points} fill="none" stroke={stroke} strokeWidth="2" />
        </svg>
      </div>
      <p className={cn("text-meta tabular-nums mt-1", deltaCls)}>
        Δ {isUp ? "+" : ""}{(Math.abs(d) >= 100 ? d.toFixed(0) : d.toFixed(1))}{suffix} over 12w · range {min.toFixed(suffix === "ms" ? 0 : 1)}–{max.toFixed(suffix === "ms" ? 0 : 1)}{suffix}
      </p>
    </div>
  );
}

function CustomerEmpty({ userName, onSetup }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="drift-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Measure · Model Performance & Drift</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          No drift signals yet. Once your agents start emitting completions
          and the drift policy is active, this page lights up with 12-week
          quality, hallucination and latency trends per agent.
        </p>
        <button type="button" onClick={onSetup} data-testid="drift-empty-cta" className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700">
          Set up agent inventory
        </button>
      </div>
    </div>
  );
}
